import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  DEFAULT_COMPANY_SETTINGS,
  type AccountDto,
  type CompanySettings,
  type CreateCustomerReceiptRequest,
  type CustomerDto,
  type CustomerReceiptDto,
  type SalesInvoiceSummary,
} from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import { api, ProblemError, UnauthorizedError } from './api';
import { formatMoney, parseAmount } from './money';
import { PrintPreview, receiptDocument } from './PrintDocument';
import ReceiptApplication from './ReceiptApplication';
import WithholdingReport from './WithholdingReport';
import Loading from './Loading';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  accounts: readonly AccountDto[];
  canRecord: boolean;
  onSessionLost: () => void;
}

const CASH_SUBLEDGERS = new Set(['cashbox', 'bank_or_wallet']);

const ISSUE_LABELS: Record<string, string> = {
  ALLOCATION_EXCEEDS_OPEN: 'المخصص أكبر من المتبقى على الفاتورة',
  ALLOCATION_EXCEEDS_AMOUNT: 'مجموع التخصيص أكبر من المتاح (النقد + الخصم من المنبع)',
  ALLOCATION_WRONG_PARTY: 'الفاتورة لعميل آخر',
  ALLOCATION_DOCUMENT_UNKNOWN: 'الفاتورة غير موجودة',
  ALLOCATION_DUPLICATE_DOCUMENT: 'الفاتورة مكررة فى التخصيص',
  ALLOCATION_NOT_POSITIVE: 'المخصص يجب أن يكون موجبًا',
  RECEIPT_DEPOSIT_ACCOUNT: 'حساب الإيداع يجب أن يكون خزينة أو بنكًا',
  CUSTOMER_NOT_FOUND: 'العميل غير موجود',
  PERIOD_CLOSED: 'الفترة المالية مقفلة',
  DATE_OUTSIDE_PERIOD: 'التاريخ خارج الفترة المالية',
  WITHHOLDING_DISABLED: 'الخصم من المنبع على التحصيل مطفأ فى الإعدادات',
  WITHHOLDING_AMOUNT_MISMATCH: 'مبلغ الخصم لا يساوى الوعاء × النسبة',
  WITHHOLDING_INPUT_MISSING: 'أدخل مبلغ الخصم، أو النسبة مع الوعاء',
  WITHHOLDING_NOT_POSITIVE: 'الخصم ونسبته ووعاؤه يجب أن تكون موجبة',
  ACCOUNT_MAP_INCOMPLETE: 'حساب مطلوب غير موجود فى دليل الشركة',
  APPLICATION_BEFORE_RECEIPT: 'التسوية قبل تاريخ الإيصال',
  APPLICATION_BEFORE_INVOICE: 'التسوية قبل تاريخ إصدار الفاتورة',
  ADVANCE_NOT_AVAILABLE: 'لا متاح على هذا الإيصال',
};

function today(): string {
  const now = new Date();
  return (
    now.getFullYear() +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0')
  );
}

/**
 * التحصيل: مبلغ يُقبض فى خزينة أو بنك ويُخصص على فواتير العميل المفتوحة. ما لم يُخصص
 * دفعة مقدمة تُسوّى لاحقًا على فاتورة. زر «الأقدم أولاً» يقترح التوزيع فقط — المستخدم
 * يراه ويعدّله. حقل الخصم من المنبع يظهر حسب إعداد الشركة، ونسبه المقترحة من الإعدادات.
 */
export default function CustomerReceipts({
  companyId,
  currency,
  accounts,
  canRecord,
  onSessionLost,
}: Props) {
  const [customers, setCustomers] = useState<CustomerDto[]>([]);
  const [receipts, setReceipts] = useState<CustomerReceiptDto[] | null>(null);
  const [customerId, setCustomerId] = useState('');
  const [openInvoices, setOpenInvoices] = useState<SalesInvoiceSummary[]>([]);
  const [receiptDate, setReceiptDate] = useState(today);
  const [amount, setAmount] = useState('');
  const [depositAccountCode, setDepositAccountCode] = useState('');
  const [externalReference, setExternalReference] = useState('');
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [submitting, setSubmitting] = useState(false);
  const [issues, setIssues] = useState<{ code: string; message: string; lineIndex?: number }[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<CustomerReceiptDto | null>(null);
  const [settings, setSettings] = useState<CompanySettings>(DEFAULT_COMPANY_SETTINGS);
  const [whRate, setWhRate] = useState('');
  const [whBase, setWhBase] = useState('');
  const [whAmount, setWhAmount] = useState('');
  const [applying, setApplying] = useState<CustomerReceiptDto | null>(null);
  const [applied, setApplied] = useState<string | null>(null);
  const [printing, setPrinting] = useState<CustomerReceiptDto | null>(null);

  const cashAccounts = useMemo(
    () =>
      accounts.filter(
        (account) =>
          account.isPostable &&
          account.subledger !== null &&
          CASH_SUBLEDGERS.has(account.subledger),
      ),
    [accounts],
  );

  const handleError = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      if (caught instanceof ProblemError) {
        setIssues(caught.problem.issues ?? []);
        setFailure(
          caught.problem.issues?.length ? null : (caught.problem.detail ?? caught.problem.title),
        );
        return;
      }
      setFailure(caught instanceof Error ? caught.message : String(caught));
    },
    [onSessionLost],
  );

  const load = useCallback(() => {
    Promise.all([api.customers(companyId), api.customerReceipts(companyId)])
      .then(([customerList, receiptList]) => {
        setCustomers(customerList);
        setReceipts(receiptList);
      })
      .catch(handleError);
  }, [companyId, handleError]);

  useEffect(load, [load]);

  // الإعدادات تحكم ظهور الخصم ووصف الباقى؛ تعذّر قراءتها يُبقى الافتراضى الموصى به
  useEffect(() => {
    api
      .companySettings(companyId)
      .then((response) => setSettings(response.settings))
      .catch(() => setSettings(DEFAULT_COMPANY_SETTINGS));
  }, [companyId]);
  const withholdingEnabled = settings.tax.withholding.onReceipts;
  const leftoverLabel =
    settings.sales.customerAdvances === 'separate_account' ? 'دفعة مقدمة' : 'دائنًا على العميل';

  useEffect(() => {
    if (!customerId) {
      setOpenInvoices([]);
      return;
    }
    api
      .salesInvoices(companyId, customerId, true)
      .then((list) =>
        // الأقدم استحقاقًا أولاً — ترتيب العرض والاقتراح
        setOpenInvoices([...list].sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1))),
      )
      .catch(handleError);
  }, [companyId, customerId, handleError, recorded]);

  function touched(): void {
    setIdempotencyKey(crypto.randomUUID());
    setIssues([]);
    setFailure(null);
    setRecorded(null);
  }

  const parsedAmount = parseAmount(amount);
  const allocationValues = openInvoices.map((invoice) =>
    parseAmount(allocations[invoice.id] ?? ''),
  );
  const allocated = allocationValues.every((value) => value !== null)
    ? Money.sum(allocationValues as string[])
    : null;
  // الخصم: نسبة على وعاء تُحسب بـ Money، أو مبلغ يُكتب. الخادم يرفض إن اختلفا
  const rate = withholdingEnabled ? parseAmount(whRate) : '0';
  const base = withholdingEnabled ? parseAmount(whBase) : '0';
  const typedWithholding = withholdingEnabled ? parseAmount(whAmount) : '0';
  const byRate =
    rate !== null && base !== null && Money.of(rate).isPositive() && Money.of(base).isPositive();
  const typedPositive = typedWithholding !== null && Money.of(typedWithholding).isPositive();
  const withholding = typedPositive
    ? Money.of(typedWithholding)
    : byRate
      ? Money.of(base).mul(rate).div('100').round(2)
      : Money.zero();
  const withholdingValid = rate !== null && base !== null && typedWithholding !== null;
  const settled =
    parsedAmount !== null && withholdingValid ? Money.of(parsedAmount).add(withholding) : null;
  const unallocated = settled !== null && allocated !== null ? settled.sub(allocated) : null;
  const ready =
    customerId !== '' &&
    depositAccountCode !== '' &&
    parsedAmount !== null &&
    Money.of(parsedAmount).isPositive() &&
    unallocated !== null &&
    !unallocated.isNegative();

  function suggestOldestFirst(): void {
    if (settled === null) return;
    let remaining = settled;
    const next: Record<string, string> = {};
    for (const invoice of openInvoices) {
      if (!remaining.isPositive()) break;
      const open = Money.of(invoice.openBalance);
      const take = remaining.lt(open) ? remaining : open;
      next[invoice.id] = take.toFixed(2);
      remaining = remaining.sub(take);
    }
    setAllocations(next);
    touched();
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready || parsedAmount === null) return;
    setSubmitting(true);
    setIssues([]);
    setFailure(null);
    const chosen = openInvoices
      .map((invoice, index) => ({ invoiceId: invoice.id, amount: allocationValues[index] ?? null }))
      .filter(
        (item): item is { invoiceId: string; amount: string } =>
          item.amount !== null && Money.of(item.amount).isPositive(),
      );
    const body: CreateCustomerReceiptRequest = {
      customerId,
      receiptDate,
      amount: parsedAmount,
      depositAccountCode,
      ...(externalReference.trim() ? { externalReference: externalReference.trim() } : {}),
      allocations: chosen,
      ...(withholding.isPositive()
        ? {
            withholding: {
              ...(byRate && rate !== null && base !== null ? { ratePercent: rate, base } : {}),
              ...(typedPositive && typedWithholding !== null ? { amount: typedWithholding } : {}),
            },
          }
        : {}),
    };
    try {
      const receipt = await api.createCustomerReceipt(companyId, idempotencyKey, body);
      setRecorded(receipt);
      setAmount('');
      setExternalReference('');
      setAllocations({});
      setWhRate('');
      setWhBase('');
      setWhAmount('');
      setIdempotencyKey(crypto.randomUUID());
      load();
    } catch (caught) {
      handleError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  const chosenIndexes = openInvoices
    .map((invoice, index) => ({ index, value: allocationValues[index] ?? null }))
    .filter((item) => item.value !== null && Money.of(item.value).isPositive())
    .map((item) => item.index);
  // فهرس التخصيص فى الطلب ≠ فهرس الصف: الطلب يحمل المخصص الموجب فقط
  const issuesOfRow = (rowIndex: number) =>
    issues.filter((issue) => issue.lineIndex === chosenIndexes.indexOf(rowIndex));
  const generalIssues = issues.filter((issue) => issue.lineIndex === undefined);

  return (
    <>
      {canRecord && (
        <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
          <h2>تحصيل من عميل</h2>
          <div className="form-row">
            <label className="grow">
              العميل
              <select
                value={customerId}
                onChange={(event) => {
                  setCustomerId(event.target.value);
                  setAllocations({});
                  touched();
                }}
              >
                <option value="">— اختر عميلاً —</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.code} — {customer.nameAr}
                  </option>
                ))}
              </select>
              <Hint>العميل الذى دفع لك.</Hint>
            </label>
            <label>
              التاريخ
              <input
                type="date"
                value={receiptDate}
                onChange={(event) => {
                  setReceiptDate(event.target.value);
                  touched();
                }}
              />
            </label>
            <label>
              المبلغ
              <input
                inputMode="decimal"
                value={amount}
                onChange={(event) => {
                  setAmount(event.target.value);
                  touched();
                }}
              />
              <Hint>المبلغ الذى استلمته فعلاً فى يدك أو حسابك.</Hint>
            </label>
          </div>
          <div className="form-row">
            <label className="grow">
              قُبض فى
              <select
                value={depositAccountCode}
                onChange={(event) => {
                  setDepositAccountCode(event.target.value);
                  touched();
                }}
              >
                <option value="">— خزينة أو بنك —</option>
                {cashAccounts.map((account) => (
                  <option key={account.code} value={account.code}>
                    {account.code} — {account.nameAr}
                  </option>
                ))}
              </select>
              <Hint>الخزينة أو البنك الذى دخلت فيه الفلوس.</Hint>
            </label>
            <label className="grow">
              مرجع (شيك/تحويل)
              <input
                value={externalReference}
                maxLength={64}
                onChange={(event) => {
                  setExternalReference(event.target.value);
                  touched();
                }}
              />
              <Hint>رقم الشيك أو التحويل البنكى، ليسهل الرجوع إليه عند المراجعة.</Hint>
            </label>
          </div>

          {withholdingEnabled && (
            <div className="form-row">
              <label>
                نسبة الخصم من المنبع %
                <input
                  inputMode="decimal"
                  list="withholding-rates"
                  value={whRate}
                  placeholder="اختيارى"
                  onChange={(event) => {
                    setWhRate(event.target.value);
                    touched();
                  }}
                />
                <datalist id="withholding-rates">
                  {settings.tax.withholding.receiptRates.map((value) => (
                    <option key={value} value={value} />
                  ))}
                </datalist>
                <Hint>
                  لو العميل شركة خصمت جزءًا من مبلغك لصالح الضرائب، اكتب النسبة هنا. وإلا اتركها
                  فارغة.
                </Hint>
              </label>
              <label>
                الوعاء
                <input
                  inputMode="decimal"
                  value={whBase}
                  placeholder="المبلغ الخاضع"
                  onChange={(event) => {
                    setWhBase(event.target.value);
                    touched();
                  }}
                />
                <Hint>
                  المبلغ الذى حُسبت عليه النسبة، كما فى شهادة الخصم التى يعطيها لك العميل.
                </Hint>
              </label>
              <label>
                مبلغ الخصم
                <input
                  inputMode="decimal"
                  value={whAmount}
                  placeholder={byRate ? withholding.toFixed(2) : 'أو اكتبه مباشرة'}
                  onChange={(event) => {
                    setWhAmount(event.target.value);
                    touched();
                  }}
                />
                <Hint>يُحسب تلقائيًا؛ طابقه مع شهادة الخصم من العميل.</Hint>
              </label>
              <span className="label grow">
                ما خصمه العميل لحساب المصلحة يُسدد الفاتورة كالنقد، ويُسجل رصيدًا لك لدى المصلحة.
                النسب القانونية يحددها مستشار الضرائب — أضفها فى الإعدادات.
              </span>
            </div>
          )}

          {customerId && (
            <>
              <div className="panel-head">
                <h3>الفواتير المفتوحة</h3>
                {openInvoices.length > 0 && (
                  <button
                    type="button"
                    className="ghost"
                    onClick={suggestOldestFirst}
                    disabled={settled === null}
                  >
                    اقترح التوزيع: الأقدم أولاً
                  </button>
                )}
              </div>
              {openInvoices.length === 0 ? (
                <p className="sub">
                  لا فواتير مفتوحة — التحصيل كله يبقى {leftoverLabel} تُسوّى على فاتورة لاحقة.
                </p>
              ) : (
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>الفاتورة</th>
                        <th>الاستحقاق</th>
                        <th>الإجمالى</th>
                        <th>المتبقى</th>
                        <th>يُخصص</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openInvoices.map((invoice, index) => (
                        <tr key={invoice.id}>
                          <td className="mono">{invoice.number}</td>
                          <td>{invoice.dueDate}</td>
                          <td className="num-cell">{formatMoney(invoice.total)}</td>
                          <td className="num-cell">{formatMoney(invoice.openBalance)}</td>
                          <td>
                            <input
                              inputMode="decimal"
                              value={allocations[invoice.id] ?? ''}
                              onChange={(event) => {
                                setAllocations((current) => ({
                                  ...current,
                                  [invoice.id]: event.target.value,
                                }));
                                touched();
                              }}
                            />
                            {issuesOfRow(index).map((issue, i) => (
                              <p key={i} className="error">
                                {ISSUE_LABELS[issue.code] ?? issue.message}
                              </p>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="sub">
                {withholding.isPositive() &&
                  'النقد + الخصم ' + (settled ? formatMoney(settled) : '—') + ' • '}
                المخصص {allocated ? formatMoney(allocated) : '—'} • يبقى {leftoverLabel}{' '}
                {unallocated ? formatMoney(unallocated) : '—'} {currency}
                {unallocated?.isNegative() ? ' — التخصيص أكبر من المبلغ' : ''}
              </p>
            </>
          )}

          {generalIssues.map((issue, i) => (
            <p key={i} className="error">
              {ISSUE_LABELS[issue.code] ?? issue.message}
            </p>
          ))}
          {failure && <p className="error">{failure}</p>}
          {recorded && (
            <p className="sub">
              سُجل الإيصال {recorded.number} بمبلغ {formatMoney(recorded.amount)}
              {Money.of(recorded.withholdingAmount).isPositive() &&
                ' وخصم منبع ' + formatMoney(recorded.withholdingAmount)}{' '}
              — قيده {recorded.journalNumber}، والباقى {leftoverLabel}{' '}
              {formatMoney(recorded.unallocated)}.
            </p>
          )}
          <div className="form-actions sticky">
            <button type="submit" className="primary" disabled={submitting || !ready}>
              {submitting ? 'جارى التسجيل…' : 'تسجيل التحصيل'}
            </button>
            <Hint block>
              يُنقص المبلغ من مديونية العميل ويضيفه للخزينة أو البنك. ليس دخلاً جديدًا — الدخل سُجّل
              يوم الفاتورة. بعد الترحيل لا يُعدَّل ولا يُحذف — الخطأ يُصحَّح بمستند عكسى، حتى يبقى
              تاريخ كل مبلغ واضحًا.
            </Hint>
          </div>
        </form>
      )}

      {applying && (
        <ReceiptApplication
          key={applying.id}
          companyId={companyId}
          currency={currency}
          receipt={applying}
          issueLabels={ISSUE_LABELS}
          defaultDate={today()}
          onDone={(application) => {
            setApplying(null);
            setApplied(
              'سُجلت التسوية ' +
                application.number +
                ' بمبلغ ' +
                formatMoney(application.amount) +
                ' — قيدها ' +
                (application.journalNumber ?? ''),
            );
            load();
          }}
          onCancel={() => setApplying(null)}
          onSessionLost={onSessionLost}
        />
      )}

      <div className="panel">
        <h2>الإيصالات</h2>
        <Hint block>
          «تسوية على فاتورة» تربط مبلغًا دفعه العميل مقدمًا بفاتورة صدرت له بعد ذلك.
        </Hint>
        {applied && <p className="sub">{applied}</p>}
        {receipts === null && !failure && <Loading />}
        {receipts?.length === 0 && <p className="sub">لا تحصيلات بعد.</p>}
        {receipts && receipts.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>الرقم</th>
                  <th>العميل</th>
                  <th>التاريخ</th>
                  <th>المبلغ</th>
                  <th>خصم المنبع</th>
                  <th>خُصص على</th>
                  <th>لم يُسوَّ</th>
                  <th>القيد</th>
                  <th />
                  {canRecord && <th />}
                </tr>
              </thead>
              <tbody>
                {receipts.map((receipt) => (
                  <tr key={receipt.id}>
                    <td className="mono">{receipt.number}</td>
                    <td>{receipt.customerName}</td>
                    <td>{receipt.receiptDate}</td>
                    <td className="num-cell">{formatMoney(receipt.amount)}</td>
                    <td className="num-cell">{formatMoney(receipt.withholdingAmount)}</td>
                    <td className="mono">
                      {receipt.allocations
                        .map((item) => item.invoiceNumber + ': ' + formatMoney(item.amount))
                        .join('، ')}
                    </td>
                    <td className="num-cell">{formatMoney(receipt.unallocated)}</td>
                    <td className="mono">{receipt.journalNumber}</td>
                    <td>
                      <button type="button" className="link" onClick={() => setPrinting(receipt)}>
                        طباعة
                      </button>
                    </td>
                    {canRecord && (
                      <td>
                        {Money.of(receipt.unallocated).isPositive() && (
                          <button
                            type="button"
                            className="ghost"
                            onClick={() => {
                              setApplied(null);
                              setApplying(receipt);
                            }}
                          >
                            تسوية على فاتورة
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {printing && (
        <PrintPreview
          companyId={companyId}
          doc={receiptDocument(
            printing,
            customers.find((customer) => customer.id === printing.customerId),
            currency,
          )}
          onClose={() => setPrinting(null)}
          onSessionLost={onSessionLost}
        />
      )}

      <WithholdingReport
        companyId={companyId}
        currency={currency}
        defaultFrom={today().slice(0, 4) + '-01-01'}
        defaultTo={today()}
        onSessionLost={onSessionLost}
      />
    </>
  );
}
